import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, ActivitySquare, Wrench, ParkingSquare, TrendingDown, ShoppingCart, AlertTriangle, CalendarClock, ArrowLeft } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../services/api';
import { PageHeader } from '../components/PageParts';
import { Button } from '../components/ui/button';

const COLORS = ['#C52429', '#3B82F6', '#F59E0B', '#6B7280', '#8B5CF6', '#EF4444', '#DC2626'];

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 font-medium">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-brand">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {sub && <p className="text-xs text-neutral-500 dark:text-neutral-400">{sub}</p>}
    </div>
  );
}

export default function FleetDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerCompany, setOwnerCompany] = useState('');
  const [companies, setCompanies] = useState([]);

  
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [s, c, comp] = await Promise.all([
          api.get('/fleet-dashboard/stats', { params: { owner_company_id: ownerCompany } }),
          api.get('/fleet-dashboard/charts', { params: { owner_company_id: ownerCompany } }),
          api.get('/masters/owner-companies', { params: { limit: 100 } }),
        ]);
        setStats(s.data);
        setCharts(c.data);
        setCompanies(comp.data.items || []);
      } catch { } finally { setLoading(false); }
    })();
  }, [ownerCompany]);


  if (loading || !stats) {
    return (
      <div>
        <PageHeader title="Dashboard da Frota" subtitle="Visão geral operacional dos veículos" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-32 rounded-xl shimmer" />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Dashboard da Frota"
        subtitle={`Idade média da frota: ${stats.average_age} anos`}
        action={
          <Button data-testid="back-to-fleet" variant="outline" onClick={() => navigate('/frota')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar para lista
          </Button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-total" icon={Truck} label="Total" value={stats.total} sub="Todos os veículos" />
        <KpiCard testid="kpi-active" icon={ActivitySquare} label="Ativos" value={stats.active} />
        <KpiCard testid="kpi-in-operation" icon={ActivitySquare} label="Em Operação" value={stats.in_operation} />
        <KpiCard testid="kpi-maintenance" icon={Wrench} label="Em Manutenção" value={stats.in_maintenance} />
        <KpiCard testid="kpi-reserve" icon={ParkingSquare} label="Reserva" value={stats.reserve} />
        <KpiCard testid="kpi-sold" icon={ShoppingCart} label="Vendidos" value={stats.sold} />
        <KpiCard testid="kpi-decommissioned" icon={TrendingDown} label="Baixados" value={stats.decommissioned} />
        <KpiCard testid="kpi-damaged" icon={AlertTriangle} label="Sinistrados" value={stats.damaged} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Veículos por Marca</h3>
          <p className="text-xs text-neutral-500 mb-4">Top 10 marcas</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.by_brand} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#C52429" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Veículos por Tipo</h3>
          <p className="text-xs text-neutral-500 mb-4">Distribuição por categoria</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.by_type} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Situação da Frota</h3>
          <p className="text-xs text-neutral-500 mb-4">Distribuição atual</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={charts.by_situation} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 11 }}>
                  {charts.by_situation.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Idade da Frota</h3>
          <p className="text-xs text-neutral-500 mb-4"><CalendarClock className="h-3 w-3 inline mr-1" />Distribuição por faixa etária</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.age_distribution} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
